import React, { useCallback, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { Prompt, useHistory } from 'react-router-dom';
import { add, endOfDay } from 'date-fns/esm';
import { toDays } from 'duration-fns';
import animateScrollTo from 'animated-scroll-to';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import type { AxiosError } from 'axios';

import { useStyles } from 'features/my-gift-cards/components/GiftCardEditorForm/styles';
import { FullBrandedGiftCard } from 'features/my-gift-cards/ts/FullBrandedGiftCard';
import { ImageFileField } from 'features/my-gift-cards/components/ImageFileField';
import { CreateBrandedGiftCard } from 'features/my-gift-cards/ts/CreateBrandedGiftCard';
import { useValidation } from 'hooks';
import { InputField } from 'shared/forms';
import { SelectInputField } from 'features/app/components/SelectInputField';
import i18n, { t } from 'locales/i18n';
import BaseButton from 'features/app/components/BaseButton';
import { CurrencySelector } from 'features/my-gift-cards/components/CurrencySelector';
import { CardPreview } from 'features/my-gift-cards/components/CardPreview';
import { useTranslation } from 'hooks/useTranslation';
import { createGiftCardRequest, editGiftCardRequest } from 'features/my-gift-cards/api';
import { toFormData } from 'features/app/utils/toFormData';
import { createGiftCardAction, updateGiftCardAction } from 'features/my-gift-cards/redux/action';
import { useMessage } from 'features/message/hooks/useMessage';
import { ErrorUtils } from 'utils';
import { parseFieldErrors } from 'utils/error-utils';
import { mapSelectOutput } from 'features/app/utils/mapSelectOutput';
import { myGiftCardsPaths } from 'features/my-gift-cards/routes/paths';
import { omitFields } from 'features/app/utils/omitFields';
import { FileUtils } from 'utils/FileUtils';
import { SelectOption } from 'features/app/components/SelectInput';
import { CartPreviewTemplateParams } from 'features/my-gift-cards/ts/CartPreviewTemplateParams';
import { BrandedGCCurrency } from '../../ts/BrandedGCCurrency';
import { clipNumberInput } from 'features/app/utils/clipNumberInput';
import { queryClient } from 'api/query';

interface GiftCardEditorFormProps {
  card?: FullBrandedGiftCard;
  invalidateQuery?: () => void;
  redirectPath?: string;
  isHidePreview?: boolean;
}

interface GiftCardEditorState extends Partial<CreateBrandedGiftCard> {}

const DEFAULT_VALUES: Partial<GiftCardEditorState> = {};

const expiryTypeOptions = [
  {
    label: t('days', { ns: 'my-gift-cards' }),
    value: 'days',
  },
  {
    label: t('weeks', { ns: 'my-gift-cards' }),
    value: 'weeks',
  },
  {
    label: t('years', { ns: 'my-gift-cards' }),
    value: 'years',
  },
];

export const GiftCardEditorForm: React.FC<GiftCardEditorFormProps> = ({
  card,
  invalidateQuery,
  redirectPath,
  isHidePreview = false,
}) => {
  const direction = i18n.dir();

  const classes = useStyles({ direction });
  const { required, isInteger } = useValidation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { showErrorMessage } = useMessage();
  const { t } = useTranslation('my-gift-cards');

  const [isPreviewMode, setIsPreviewMode] = React.useState(false);

  const mappedCard = React.useMemo(() => {
    if (!card) return;
    return {
      ...card,
      expiry_type: expiryTypeOptions.find(item => item.value === card.expiry_type),
    } as unknown as GiftCardEditorState;
  }, [card]);

  const form = useForm<GiftCardEditorState>({
    mode: 'onChange',
    defaultValues: mappedCard ?? DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty, errors },
    trigger,
    setError,
    watch,
    setValue,
  } = form;

  const [currencies, banner_image, brand_name, product_name, gift_card_image, expiry, expiry_type] = watch([
    'currencies',
    'banner_image',
    'brand_name',
    'product_name',
    'gift_card_image',
    'expiry',
    'expiry_type',
  ]);

  const onSubmit = async (formState: GiftCardEditorState) => {
    if (!isCurrenciesValid()) {
      setError('currencies', { message: t('productsCurrencyValidationMessage') });
      return;
    }

    const serialized = toFormData(
      mapSelectOutput(
        ['expiry_type'],
        omitFields(card ? ['banner_image', 'gift_card_image'] : [], formState, value => typeof value === 'string')
      )
    );

    try {
      if (card) {
        const { data: updatedCard } = await editGiftCardRequest(card.id, serialized);
        dispatch(
          updateGiftCardAction({
            banner_image: updatedCard.banner_image,
            brand_name: updatedCard.brand_name,
            gift_card_image: updatedCard.gift_card_image,
            id: updatedCard.id,
          })
        );
        invalidateQuery && invalidateQuery();
      } else {
        const { data: newCard } = await createGiftCardRequest(serialized);
        dispatch(
          createGiftCardAction({
            banner_image: newCard.banner_image,
            brand_name: newCard.brand_name,
            gift_card_image: newCard.gift_card_image,
            id: newCard.id,
          })
        );
      }

      queryClient.invalidateQueries('branded-gift-cards-options');

      redirectPath ? history.push(redirectPath) : history.push(myGiftCardsPaths.static.root);
    } catch (error_) {
      const error = error_ as AxiosError;

      parseFieldErrors<CreateBrandedGiftCard>(error.response?.data).forEach(({ field, ...error }) =>
        setError(field, error)
      );
      showErrorMessage(ErrorUtils.convertError(error));
    }
  };

  const handleModeChange = async () => {
    if (!isPreviewMode) {
      if (!isCurrenciesValid()) {
        setError('currencies', { message: t('productsCurrencyValidationMessage') });
        return;
      }
      trigger();
    }
    if (isReallyValid) {
      await animateScrollTo(0);

      setIsPreviewMode(value => !value);
    }
  };

  const isReallyValid = useMemo(() => {
    return isValid && Object.keys(errors).length === 0;
  }, [isValid, errors]);

  const isCurrenciesValid = useCallback(() => {
    if (!currencies || (currencies as BrandedGCCurrency[])?.length === 0) return false;
    return true;
  }, [currencies]);

  const templateParams = React.useMemo((): CartPreviewTemplateParams => {
    const duration = {
      [((expiry_type as unknown as SelectOption)?.value as keyof Duration) ?? 'days']: Number(expiry ?? 0),
    };

    return {
      banner_image: FileUtils.toObjectUrl(banner_image as File),
      brand_name: brand_name as string,
      product_name: product_name as string,
      card_image: FileUtils.toObjectUrl(gift_card_image as File),
      expiresAt: endOfDay(add(Date.now(), duration)),
      expiry_days: toDays(duration),
    };
  }, [banner_image, brand_name, product_name, gift_card_image, expiry, expiry_type]);

  const handleValidityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue('expiry', clipNumberInput(e.target.value, 1, Infinity));
  };

  return (
    <div className={classes.root}>
      {<Prompt when={!isSubmitting && isDirty} message={t('formLeavingPrompt', { ns: 'common' })} />}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <CardPreview show={isPreviewMode} templateParams={templateParams} />
          <Box display={isPreviewMode ? 'none' : 'block'}>
            <ImageFileField
              name="banner_image"
              titleLabelText={t('coverImagePlaceholder')}
              containerClass={classes.bannerImageInput}
              rules={{ validate: required }}
              strictWidth={1240}
              strictHeight={393}
            />

            <Box display="flex" justifyContent="space-between" marginTop={16}>
              <Box flex={0.3} className={classes.giftCardImageWrapper}>
                <ImageFileField
                  name="gift_card_image"
                  titleLabelText={t('cardImagePlaceholder')}
                  containerClass={classes.cardImageInput}
                  rules={{ validate: required }}
                  strictWidth={1016}
                  strictHeight={639}
                />
              </Box>
              <Box flex={0.6} marginTop={10}>
                <InputField
                  name="product_name"
                  fullWidth
                  placeholder={t('inputs.enter', { ns: 'common' })}
                  label={t('productName')}
                  required
                  type="text"
                  rules={{
                    validate: required,
                  }}
                />
                <Box className={classes.input}>
                  <InputField
                    name="brand_name"
                    fullWidth
                    placeholder={t('inputs.enter', { ns: 'common' })}
                    label={t('brandName')}
                    required
                    type="text"
                    rules={{
                      validate: required,
                    }}
                  />
                </Box>

                <Box display="flex" flexDirection="row" alignItems="flex-end" marginTop={16}>
                  <Box paddingRight={6} className={classes.validityCardContainer}>
                    <InputField
                      name="expiry"
                      fullWidth
                      placeholder={t('validityPlaceholder')}
                      label={t('validity')}
                      required
                      type="number"
                      onChange={handleValidityChange}
                      rules={{
                        validate: {
                          ...required,
                          isInteger,
                        },
                      }}
                    />
                  </Box>
                  <Box paddingLeft={6} className={classes.validityCardContainer}>
                    <SelectInputField
                      name="expiry_type"
                      label=""
                      placeholder={t('validityRangePlaceholder')}
                      options={expiryTypeOptions}
                      rules={{
                        validate: {
                          ...required,
                        },
                      }}
                    />
                  </Box>
                </Box>
                <Box marginTop={15}>
                  <CurrencySelector name="currencies" />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box display="flex" justifyContent="center" marginTop={43}>
            <Box className={classes.bottomControls}>
              {!isHidePreview && (
                <BaseButton onClick={handleModeChange} disabled={isSubmitting || !isReallyValid}>
                  {isPreviewMode ? t('back', { ns: 'common' }) : t('preview')}
                </BaseButton>
              )}
              <BaseButton type="submit" labelClass={classes.addButton} disabled={isSubmitting || !isReallyValid}>
                {card ? t('save', { ns: 'common' }) : t('add', { ns: 'common' })}
              </BaseButton>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </div>
  );
};
